import React, { useId, useState } from 'react'
import styled from 'styled-components'
import type { FlagProps } from '@awazevr/design-system.components.flags'
import { ChevronDown, ChevronUp } from '@awazevr/design-system.components.icon'
import { Text } from '@awazevr/design-system.components.text'

export type LanguageOption = {
  locale: string
  languageLabel: string
  currencyLabel: string
  flag: (props: FlagProps) => React.ReactElement
}

type LanguageSelectorProps = {
  options: LanguageOption[]
  currentLocale: string
  onSelect: (locale: string) => void
  label?: string
  /** Content on the left of the toggle, e.g. a logo or back button. */
  leading?: React.ReactNode
}

const OPTIONS_HEIGHT_PX = 240

const Root = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  border: 0;
  background: transparent;
  margin-left: auto;
  color: inherit;
  font: inherit;
  cursor: pointer;
`

const FlagWrapper = styled.span`
  display: inline-flex;
  width: 24px;
  height: 24px;

  svg {
    width: 100%;
    height: 100%;
  }
`

// Expanded by default; `aria-hidden="true"` is the collapsed state, so the
// styles follow the same attribute screen readers use.
const OptionsList = styled.ul`
  box-sizing: border-box;
  width: 100%;
  max-height: ${OPTIONS_HEIGHT_PX}px;
  height: ${OPTIONS_HEIGHT_PX}px;
  margin: 8px 0 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
  border: 1px solid ${({ theme }) => theme.ColorGrey100};
  border-radius: 8px;
  background: #fff;
  visibility: visible;
  pointer-events: auto;
  transition:
    height 200ms ease,
    margin 200ms ease,
    visibility 0s linear 0s;

  &[aria-hidden='true'] {
    height: 0;
    margin: 0;
    overflow-y: hidden;
    border-width: 0;
    visibility: hidden;
    pointer-events: none;
    transition:
      height 200ms ease,
      margin 200ms ease,
      visibility 0s linear 200ms;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &[aria-hidden='true'] {
      transition: none;
    }
  }
`

const SelectedDot = styled.span`
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.ColorButtonSolidPrimaryBackground};
`

const OptionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${({ theme }) => theme.ColorGrey50};
  }
`

// Language changes are a full navigation so getServerSideProps reruns and the
// Contentful copy loads in the new locale. The query (the reservation context)
// is kept.
export const buildLanguageChangeUrl = ({
  basePath,
  locale,
  search,
}: {
  basePath: string
  locale: string
  search: string
}): string => `${basePath}/${locale}/sign-up${search}`

export const getLanguageCode = (locale: string): string =>
  locale.split('-')[0].toUpperCase()

export const LanguageSelector = ({
  options,
  currentLocale,
  onSelect,
  label = 'Language',
  leading,
}: LanguageSelectorProps): React.ReactElement => {
  const [isExpanded, setIsExpanded] = useState(false)
  const listId = useId()
  const current = options.find((option) => option.locale === currentLocale)
  const CurrentFlag = current?.flag

  return (
    <Root data-testid="language-selector">
      <Row>
        {leading}
        <Toggle
          type="button"
          aria-expanded={isExpanded}
          aria-controls={listId}
          aria-label={label}
          data-testid="language-selector-toggle"
          onClick={() => setIsExpanded((expanded) => !expanded)}
        >
          {CurrentFlag && (
            <FlagWrapper aria-hidden="true">
              <CurrentFlag />
            </FlagWrapper>
          )}
          <Text sizes={100} as="span">
            {getLanguageCode(currentLocale)}
          </Text>
          {isExpanded ? (
            <ChevronUp size="small" />
          ) : (
            <ChevronDown size="small" />
          )}
        </Toggle>
      </Row>
      <OptionsList
        id={listId}
        aria-hidden={!isExpanded}
        data-testid="language-selector-options"
      >
        {options.map(({ locale, languageLabel, currencyLabel, flag: Flag }) => (
          <li key={locale}>
            <OptionButton
              type="button"
              aria-current={locale === currentLocale ? 'true' : undefined}
              data-testid={`language-option-${locale}`}
              onClick={() => onSelect(locale)}
            >
              <FlagWrapper aria-hidden="true">
                <Flag />
              </FlagWrapper>
              <Text sizes={100} as="span">
                {`${languageLabel} – ${currencyLabel}`}
              </Text>
              {locale === currentLocale && (
                <SelectedDot
                  aria-hidden="true"
                  data-testid="language-selected-dot"
                />
              )}
            </OptionButton>
          </li>
        ))}
      </OptionsList>
    </Root>
  )
}
